import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { loanService } from '@/services/loan.service';
import { mapFrontendToBackend, mapBackendToFrontend } from '@/utils/loan-mapping';
import { LoanFormData } from '@/schemas/loanSchema';
import { Loan } from '@/types/loan';
import toast from 'react-hot-toast';
import { useCreateLoan, useUpdateLoan } from '@/pages/loans/hooks/useLoans';
import type { LoanCreate } from '@/types/api.types';

export const useLoanForm = () => {
  const navigate = useNavigate();
  const { loanId } = useParams<{ loanId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!!loanId);
  const initialBackendDataRef = useRef<LoanCreate | null>(null);

  // React Query mutations
  const createLoanMutation = useCreateLoan();
  const updateLoanMutation = useUpdateLoan();

  const form = useForm<LoanFormData>({
    // resolver: zodResolver(loanSchema), // Temporarily disabled for testing
    mode: 'onSubmit',
    defaultValues: {
      system_id: 1,
      pricing_scenario: 'RPX Pricing',
      credit_spread: 0,
      loan_identification: {
        client_loan_number: '',
        loan_name: '',
      },
      property_details: {
        sector: 'Multifamily',
        property_type: '',
        property_lifecycle_financing: 'Permanent',
      },
      borrower: {
        sponsor_borrower: '',
      },
      loan_amounts: {
        original_balance: 0,
        current_balance: 0,
        currency: 'USD',
        client_percentage: 1.0, // 100% in decimal format
        pik_balance: '',
      },
      loan_structure: {
        position_in_capital_stack: 'Senior Loan',
        amortization_type: 'Amortizing',
        periodicity: 'Monthly',
        interest_day_count: '30/360',
        accrual_type: 'Accrued Interest',
        commitment_type: 'Closed End',
      },
      interest_terms: {
        interest_type: 'Fixed',
      },
      dates: {
        origination_date: '',
        first_payment_date: '',
        original_maturity_date: '',
      },
      loan_status: {
        status: 'Performing',
        commentary: '',
      },
      property_locations: [
        {
          street: '',
          city: '',
          state: '',
          country: 'United States',
          region: 'United States',
        },
      ],
      prepayment_terms: {
        prepayment_penalty_type: null,
      },
      step_up: {
        step_up_date: null,
        step_up_incremental_rate: null,
        step_up_coupons: null,
        step_up_margin_interest_rate_floor: null,
      },
    },
  });

  const buildPartialUpdate = (
    initial: LoanCreate,
    current: LoanCreate
  ): Partial<LoanCreate> => {
    const diff: Partial<LoanCreate> = {};

    (Object.keys(current) as (keyof LoanCreate)[]).forEach((key) => {
      const initialVal = initial[key];
      const currentVal = current[key];

      const isObject =
        typeof currentVal === 'object' && currentVal !== null && !Array.isArray(currentVal);

      const valuesEqual = isObject || Array.isArray(currentVal)
        ? JSON.stringify(initialVal) === JSON.stringify(currentVal)
        : initialVal === currentVal;

      if (!valuesEqual) {
        // @ts-expect-error – assigning dynamically by key is safe here
        diff[key] = currentVal;
      }
    });

    return diff;
  };

  // Load loan data on mount (for edit mode only)
  useEffect(() => {
    const loadData = async () => {
      if (!loanId) return;

      setIsLoading(true);
      try {
        const numericId = parseInt(loanId, 10);

        // 1) Пытаемся получить полный объект лоана по /api/v1/loans/{id}
        //    (как в твоём примере), где уже есть все даты.
        let backendLoan: unknown;
        try {
          const loanById = await loanService.getLoanById(numericId);
          backendLoan = loanById;
          console.log(
            'Loaded loan via getLoanById (full response with dates):',
            JSON.stringify(loanById, null, 2)
          );
        } catch (e) {
          console.warn(
            'getLoanById failed, falling back to getLoanBySystemId:',
            e
          );
          // 2) Фоллбек – старый структурированный эндпоинт
          const loanBySystemId = await loanService.getLoanBySystemId(
            numericId
          );
          backendLoan = loanBySystemId;
          console.log(
            'Loaded loan via getLoanBySystemId:',
            JSON.stringify(loanBySystemId, null, 2)
          );
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loanData = mapBackendToFrontend(backendLoan as any);

        // Save initial backend-shaped payload for diffing on submit
        initialBackendDataRef.current = mapFrontendToBackend(
          loanData as Loan
        ) as LoanCreate;

        console.log('Mapped loan data for form:', loanData);
        form.reset(loanData as LoanFormData);
        setIsEditMode(true);
      } catch (error) {
        console.error('Error loading loan:', error);
        toast.error('Failed to load loan data');
        navigate('/loans');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [loanId, form.reset, toast, navigate]);

  // Handle form submission
  const onSubmit = async (data: LoanFormData) => {
    console.log('Form submission started with data:', data);
    setIsSubmitting(true);
    try {
      console.log('Mapping frontend data to backend format...');
      let mappedData: LoanCreate;
      try {
        mappedData = mapFrontendToBackend(data as Loan) as LoanCreate;
        console.log('Mapped data for API:', mappedData);
      } catch (mappingError) {
        console.error('Error during data mapping:', mappingError);
        throw mappingError;
      }

      if (isEditMode && loanId) {
        const payload =
          initialBackendDataRef.current != null
            ? buildPartialUpdate(initialBackendDataRef.current, mappedData)
            : mappedData;

        // Update existing loan using mutation
        updateLoanMutation.mutate(
          {
            systemId: parseInt(loanId),
            data: payload,
          },
          {
            onSuccess: () => {
              // Redirect to results page
              navigate(`/loans`);
            },
            onError: (error) => {
              // Handle validation errors
              if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as {
                  response?: { status: number; data: { detail: unknown } };
                };
                if (axiosError.response?.status === 422) {
                  const validationErrors = axiosError.response.data.detail;
                  if (Array.isArray(validationErrors)) {
                    validationErrors.forEach(
                      (err: { loc: string[]; msg: string }) => {
                        const fieldPath = err.loc.join('.');
                        console.error(
                          `Validation error at ${fieldPath}: ${err.msg}`
                        );
                      }
                    );
                  }
                }

                toast.error(
                  axiosError.response?.data?.detail
                    ? String(axiosError.response.data.detail)
                    : 'Failed to update loan'
                );
              } else {
                toast.error('Failed to update loan');
              }
            },
          }
        );
      } else {
        // Create new loan using mutation
        createLoanMutation.mutate(mappedData, {
          onSuccess: () => {
            // Redirect to results page
            navigate(`/loans`);
          },
          onError: (error) => {
            // Handle validation errors
            if (error && typeof error === 'object' && 'response' in error) {
              const axiosError = error as {
                response?: { status: number; data: { detail: unknown } };
              };
              if (axiosError.response?.status === 422) {
                const validationErrors = axiosError.response.data.detail;
                if (Array.isArray(validationErrors)) {
                  validationErrors.forEach(
                    (err: { loc: string[]; msg: string }) => {
                      const fieldPath = err.loc.join('.');
                      console.error(
                        `Validation error at ${fieldPath}: ${err.msg}`
                      );
                    }
                  );
                }
              }

              toast.error(
                axiosError.response?.data?.detail
                  ? String(axiosError.response.data.detail)
                  : 'Failed to create loan'
              );
            } else {
              toast.error('Failed to create loan');
            }
          },
        });
      }
    } catch (error) {
      console.error('Error during form submission:', error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} loan`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addPropertyLocation = () => {
    const currentLocations = form.watch('property_locations');
    form.setValue('property_locations', [
      ...currentLocations,
      {
        street: '',
        city: '',
        state: '',
        country: 'United States',
        region: 'United States',
      },
    ]);
  };

  const removePropertyLocation = (index: number) => {
    const currentLocations = form.watch('property_locations');
    if (currentLocations.length > 1) {
      form.setValue(
        'property_locations',
        currentLocations.filter((_, i) => i !== index)
      );
    }
  };

  return {
    form,
    isSubmitting:
      isSubmitting ||
      createLoanMutation.isPending ||
      updateLoanMutation.isPending,
    isLoading,
    isEditMode,
    onSubmit,
    addPropertyLocation,
    removePropertyLocation,
  };
};
